import { useCallback } from "react";
import { APIResponse } from "../api/ModelFactory";
import { Permission, PermissionMap } from "../api/common/models/PermissionMap";
import { useKeycloak } from "@react-keycloak/web";
import { useQuery } from "../api/state";

export type PermissionsResolver = { getPermissions: () => Promise<APIResponse<PermissionMap>> }

/**
 * Whether the permission map grants anything at all. A user without any tenant role gets an empty capabilities
 * response.
 */
export const hasAnyPermission = (permissions: PermissionMap | null | undefined): boolean => {
  return !!permissions && Object.values(permissions).some(p => Array.isArray(p) && p.length > 0)
}

export const usePermissions = (api: PermissionsResolver) => {
  const { keycloak } = useKeycloak()

  const { data: permissions, isLoading } = useQuery(['permissions'], async () => {
    // The permissions are cached in sessionStorage per user, for the lifetime of the browser tab. When a user's role
    // is changed (Care role editor or "Edit role" on a contact), the new permissions only take effect in a new
    // session. The backend enforces the current role on every request regardless.
    const key = `permissions-${keycloak.idTokenParsed?.sub ?? 'unknown'}`
    const cachedPermissions = window.sessionStorage.getItem(key)
    if (cachedPermissions) {
      return JSON.parse(cachedPermissions)
    }
    const response = await api.getPermissions()
    if (!response.success) {
      return null
    }
    // An empty map is not cached, so a user without a role can log in as soon as they get one, without having
    // to open a new tab.
    if (hasAnyPermission(response.data)) {
      window.sessionStorage.setItem(key, JSON.stringify(response.data))
    }
    return response.data
  }, { staleTime: 5 * 60 * 1000 })

  /**
   * The capabilities list an action if the user may do it on any row, e.g. only on their own customers' rows. On a
   * detail page, pass the item's permissions to check the action for that row.
   */
  const can = useCallback((permission: Permission, resource: string, item?: PermissionMap | null) => {
    if (item?.[resource]) {
      return item[resource].includes(permission)
    }
    if (!permissions) {
      return false
    }
    if (!permissions[resource]) {
      console.warn('no permissions for resource', resource)
      return false
    }
    // Missing permissions are normal with tenant roles, so only an unknown resource (a typo) is logged above.
    return permissions[resource].includes(permission)
  }, [permissions])

  return { can, permissions: permissions as PermissionMap | null, isLoading }
}
